<template>
  <div class="gas-detail">
    <div class="gas-stage">
      <!-- ===== 顶部信息栏 ===== -->
      <header class="topbar panel">
        <div class="brand">
          <div class="logo">
            <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
              <path d="M24 4 41 14v20L24 44 7 34V14z" stroke="#74efff" stroke-width="2.4"/>
              <path d="M26 12 16 27h7l-2 10 11-16h-7z" fill="#00aeff"/>
            </svg>
          </div>
          <div class="brand-txt">
            <h1>工位详情看板</h1>
            <p>GAS STATION DETAIL</p>
          </div>
          <span class="mode-tag">{{ zone }}区 · 流程区</span>
        </div>

        <!-- 本单元（两桌）核心指标 -->
        <div class="metrics">
          <div class="metric">
            <span class="m-label">工位总数</span>
            <span class="m-value"><b>{{ stats.total }}</b><small>个</small></span>
          </div>
          <div class="metric">
            <span class="m-label">在制工位</span>
            <span class="m-value c-cyan"><b>{{ stats.ok + stats.warn + stats.err }}</b></span>
          </div>
          <div class="metric">
            <span class="m-label">暂停工位</span>
            <span class="m-value c-warn"><b>{{ stats.warn }}</b></span>
          </div>
          <div class="metric">
            <span class="m-label">停工工位</span>
            <span class="m-value c-red"><b>{{ stats.err }}</b><small>个</small></span>
          </div>
          <div class="metric">
            <span class="m-label">本班完工</span>
            <span class="m-value c-green"><b>{{ doneTotal }}</b><small>台</small></span>
          </div>
        </div>

        <!-- 时钟 / 班次 -->
        <div class="clock-box">
          <div class="clock-time">{{ now }}</div>
          <div class="clock-sub"><span>{{ dateText }}</span><span class="shift">{{ shift }}</span></div>
        </div>

        <!-- 总览位置选择：图标展开迷你排布图（7 行 × 4 列 = 28 组，与总览 14 行 × 4 列一一对应），点格子切换查看 -->
        <div class="map-sel" :class="{ open: showMap }">
          <button
            class="map-btn"
            type="button"
            :title="`总览位置选择（当前第 ${pair.index + 1} / ${pairs.length} 组）`"
            :aria-expanded="showMap ? 'true' : 'false'"
            @click="toggleMap"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7" rx="1.6"/>
              <rect x="14" y="3" width="7" height="7" rx="1.6"/>
              <rect x="3" y="14" width="7" height="7" rx="1.6"/>
              <rect x="14" y="14" width="7" height="7" rx="1.6"/>
            </svg>
          </button>

          <div v-show="showMap" class="map-panel">
            <div class="mp-head">
              <span class="mp-title">总览位置</span>
              <span class="mp-no">第 {{ pair.index + 1 }} / {{ pairs.length }} 组</span>
            </div>
            <!-- 格子顺序 = 组的生成顺序：每行 4 组（4 列），每 2 行工位组成 1 组 → 面板第一行即总览第 1-2 行 -->
            <div class="mp-grid">
              <button
                v-for="p in pairs"
                :key="p.index"
                type="button"
                class="mp-cell"
                :class="['st-' + pairState(p), { on: p.index === pairIndex }]"
                :title="`第 ${p.index + 1} 组 · ${p.code} · ${p.stationCount} 个工位`"
                @click="goPair(p.index)"
              ></button>
            </div>
            <div class="mp-foot" :title="pair.code">{{ pair.code }}</div>
          </div>
        </div>

        <!-- 全屏 / 退出全屏：整个看板铺满屏幕（原生全屏，不支持时自动退回铺满视口） -->
        <button
          class="fs-btn"
          type="button"
          :title="fs ? '退出全屏（Esc）' : '全屏显示'"
          :aria-label="fs ? '退出全屏' : '全屏显示'"
          @click="toggleFs"
        >
          <svg v-if="!fs" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <!-- 返回工位总览（上一级） -->
        <button class="back-link" type="button" title="返回工位总览（气体）" @click="goOverview">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14.5 5.5 8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>返回总览</span>
        </button>

        <!-- 返回主页 -->
        <button class="back-btn" type="button" title="返回主页" @click="backHome">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M11.34 3.28a1 1 0 0 1 1.32 0l8.4 7.36a1 1 0 0 1-.66 1.75H19.4v7.4a1.4 1.4 0 0 1-1.4 1.4h-3.4v-5.6h-5.2v5.6H6a1.4 1.4 0 0 1-1.4-1.4v-7.4H3.6a1 1 0 0 1-.66-1.75z"
              fill="currentColor"
            />
          </svg>
        </button>
      </header>

      <!-- ===== 下钻单元详情：布局与总览一一对应 —— 一张桌一行、行内横排该桌工位（普通行 2 行 × 6 列，第 7-10 行 2 行 × 2 列） ===== -->
      <main class="board panel">
        <div class="board-head">
          <div class="b-left">
            <span class="z-mark"></span>
            <h2>{{ pair.code }}</h2>
            <!-- <span class="z-meta">{{ layoutText }}</span> -->
          </div>
          <div class="b-right">
            <span class="hint">点击工位查看详情</span>
            <!-- 接口失败提示 -->
            <span v-if="loadErr" class="hint" style="color: #ff9b9b;">{{ loadErr }}</span>
            <!-- SSE 断连提示（EventSource 会自动重连，恢复后自动消失） -->
            <span v-else-if="sseErr" class="hint" style="color: #ffe08a;">{{ sseErr }}</span>
            <i class="rc c-cyan">在制 <b>{{ stats.ok + stats.warn + stats.err }}</b>/{{ stats.total }}</i>
            <i class="rc c-warn">暂停 <b>{{ stats.warn }}</b></i>
            <i class="rc c-red">停工 <b>{{ stats.err }}</b></i>
            <i class="rc c-id">空闲 <b>{{ stats.idle }}</b></i>
          </div>
        </div>

        <div class="board-body">
          <div class="pair-tables">
            <div
              v-for="t in pair.tables"
              :key="t.code"
              class="d-table"
              :class="['st-' + tableState(t), { thin: pair.thin }]"
            >
              <div class="dt-head">
                <b class="dt-code">{{ t.code }}</b>
                <span class="dt-meta">{{ t.stations.length }} 工位 · 在制 {{ busyCount(t) }}</span>
                <span class="dt-flag">{{ statusTxt[tableState(t)] }}</span>
              </div>

              <!-- 每桌一行、工位数即列数（与总览里这一桌工位横排完全一致） -->
              <div class="dt-body" :style="{ '--cols': t.stations.length }">
                <button
                  v-for="st in t.stations"
                  :key="st.id"
                  type="button"
                  class="d-ws"
                  :class="['st-' + st.status, { on: activeId === st.id }]"
                  :title="`${st.id} ·  ${statusTxt[st.status]}（点击查看详情）`"
                  @click="openStation(st, t)"
                >
                  <span class="dw-top">
                    <!-- 工位序号（末两位编号，与总览页工位小图标一致） -->
                    <em class="dw-no">{{ st.short }}</em>
                    <i class="dw-dot"></i>
                    <!-- 工序位改为显示产品编号 -->
                    <b class="dw-stage" :title="fullTip('产品编号', st.product && st.product.serialCode)">{{ st.product ? st.product.serialCode : '—' }}</b>
                    <!-- 有问题的产品显示一个感叹号，没问题不显示（颜色仍跟工位状态色） -->
                    <em v-if="hasIssue(st)" class="dw-state" title="该产品存在问题">!</em>
                  </span>
                  <!-- 卡片主行 = 在制产品的产品型号 -->
                  <b class="dw-model" :title="fullTip('在制型号', st.product && st.product.model)">{{ st.product ? st.product.model : '未绑定产品' }}</b>

                  <!-- 在制产品主要信息：产品名称 / 短代码 / 合同号 / 任务单号 / 业主 / 量程
                       （✅ 按需求去掉「产品进程」，换成「合同号」）
                       卡片横向空间有限，值过长时不再只截断，而是在行内缓缓滚动显示：
                       v-marquee 会量出超出的宽度写进 --kv-shift，只有真的超宽才加动画 -->
                  <span class="dw-kv">
                    <span class="kv-row" :title="fullTip('产品名称', st.product && st.product.name)">
                      <em>产品名称</em><span class="kv-val" v-marquee><span>{{ st.product ? (st.product.name || '—') : '—' }}</span></span>
                    </span>
                    <span class="kv-row" :title="fullTip('短代码', st.product && st.product.fsn)">
                      <em>短代码</em><span class="kv-val" v-marquee><span>{{ st.product ? (st.product.fsn || '—') : '—' }}</span></span>
                    </span>
                    <span class="kv-row" :title="fullTip('合同号', st.product && (st.product.contractNo || st.product.contract))">
                      <em>合同号</em><span class="kv-val" v-marquee><span>{{ st.product ? (st.product.contractNo || st.product.contract || '—') : '—' }}</span></span>
                    </span>
                    <span class="kv-row" :title="fullTip('任务单号', st.product && st.product.taskNumber)">
                      <em>任务单号</em><span class="kv-val" v-marquee><span>{{ st.product ? (st.product.taskNumber || '—') : '—' }}</span></span>
                    </span>
                    <span class="kv-row" :title="fullTip('业主', st.product && st.product.owner)">
                      <em>最终业主</em><span class="kv-val" v-marquee><span>{{ st.product ? (st.product.owner || '—') : '—' }}</span></span>
                    </span>
                    <span class="kv-row" :title="fullTip('量程', st.product && st.product.range)">
                      <em>量程</em><span class="kv-val" v-marquee><span>{{ st.product ? (st.product.range || '—') : '—' }}</span></span>
                    </span>
                  </span>

                  <!-- 底部 = 进度条 + 文字（所有卡片都渲染，保证等高对齐）：
                       正常卡：进度条按产品进程（0/25/50/75/100%），文字为进程名
                       暂停 / 停工卡：进度条仍按产品进程，文字为暂停 / 停工原因
                       空闲卡：灰底占位 -->
                  <span class="dw-note" :class="{ 'is-free': !st.product }">
                    <template v-if="st.product">
                      <i class="dw-bar"><i :style="{ width: progressPct(st) + '%' }"></i></i>
                      <b class="dw-note-txt" :title="fullTip('状态', noteText(st))">{{ noteText(st) }}</b>
                    </template>
                    <b v-else class="dw-note-txt">工位空闲，未绑定产品</b>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <!-- ===== 底部实时告警滚动条 ===== -->
      <!-- <footer class="ticker panel">
        <span class="ticker-label"><i></i>实时告警</span>
        <div class="ticker-viewport">
          <div ref="tickerTrack" class="ticker-track">
            <span v-for="(a, i) in tickerItems" :key="i" class="tk" :class="a.sev"><b>{{ a.time }}</b>{{ a.text }}</span>
          </div>
        </div>
      </footer> -->
    </div>

    <!-- ===== 工位详情弹层 ===== -->
    <div v-if="active" class="ws-mask" @click.self="closeStation">
      <div class="ws-modal" :class="'st-' + active.st.status">
        <div class="wm-head">
          <i class="wm-dot"></i>
          <b class="wm-id">{{ active.st.id }}</b>
       
          <span class="wm-state">{{ statusTxt[active.st.status] }}</span>

          <!-- 绑了产品就能看：部件 / 自检确认 / 自检记录 / 问题 四个列表
               （同 productProgress 里点产品编号调的接口 getProductInfoList） -->
          <button
            v-if="active.st.product"
            class="wm-issue-btn"
            type="button"
            :class="{ on: hasIssue(active.st) }"
            :disabled="partsLoading"
            :title="hasIssue(active.st) ? '该产品有问题 · 查看部件 / 自检确认 / 自检记录 / 问题' : '查看部件 / 自检确认 / 自检记录 / 问题'"
            @click="openProductInfo(active.st)"
          >
            <em v-if="hasIssue(active.st)">!</em>{{ partsLoading ? '加载中…' : '产品详情' }}
          </button>

          <button class="wm-close" type="button" title="关闭（Esc）" @click="closeStation">✕</button>
        </div>

        <div class="wm-hero">
          <div class="wm-hero-main" v-tip="fullTip('在制型号', active.st.product && active.st.product.model)">
            <span class="wm-hero-label">在制型号</span>
            <b>{{ active.st.product ? active.st.product.model : '未绑定产品' }}</b>
          </div>
          <div class="wm-hero-side" v-tip="fullTip('产品进程', processText(active.st.product && active.st.product.processStatus))">
            <span class="wm-hero-label">产品进程</span>
            <b>{{ processText(active.st.product && active.st.product.processStatus) }}</b>
          </div>
        </div>

        <!-- 字段框里的值过长被省略号截断时，悬浮该框显示完整内容；
             现场是触摸屏，鼠标悬浮（title）点不出来，改用 v-tip：点一下框就弹出同样的完整信息，
             再点一下 / 点别处 / 滚屏 / Esc 关掉 -->
        <div class="wm-fields">
          <div v-for="f in detailFields" :key="f.label" class="wm-field" v-tip="fullTip(f.label, f.value)">
            <span>{{ f.label }}</span><b>{{ f.value }}</b>
          </div>
        </div>

        <!-- 底部 = 进度条 + 文字：与工位卡片的 dw-note 同一种显示方式
             进度条按产品进程（0/25/50/75/100%），文字：正常 = 进程名，暂停 / 停工 = 原因 -->
        <div class="wm-note" :class="{ 'is-free': !active.st.product }" v-tip="fullTip('状态', noteText(active.st))">
          <i class="wm-bar"><i :style="{ width: progressPct(active.st) + '%' }"></i></i>
          <b class="wm-note-txt">{{ noteText(active.st) }}</b>
        </div>
      </div>
    </div>

    <!-- 产品详情弹窗：部件 / 自检确认 / 自检记录 / 问题 四表格切换
         （复用产品列表页同一个组件 + 同一个接口 getProductInfoList） -->
    <product-part-info-dialog
      class="parts-dialog-dark"
      :visible="partsDialogVisible"
      :title="'工位详情 - 产品编号: ' + currentPartsProductCode"
      :productCode="currentPartsProductCode"
      :partsList="partsList"
      :inspectionList="inspectionList"
      :resInspectionList="resInspectionList"
      :issueList="partsIssueList"
      @close="closePartsDialog"
    />
  </div>
</template>

<script>
import UserInfoUtils from '@/utils/userInfoUtils.js';
/* 工位数据（真实接口 getStationList）+ 文案字典：与工位总览（StationViewGas）
   共用同一套数据层 ./js/stationGasData.js，保证「第 N 组」两边一一对应 */
import {
  STATUS_TXT, processText, executeText, pad2, nowTime, fetchBoard, openStationStream, ZONE_CODE,
} from './js/stationGasData.js';
/* 产品详情：部件 / 自检确认 / 自检记录 / 问题（与产品列表页 productProgress 同一接口 + 同一弹窗组件） */
import { getProductInfoList } from '@/api/mainapi.js';
import ProductPartInfoDialog from '@/components/card/ProductPartInfoDialog.vue';
/* 全屏开关：优先原生 Fullscreen API，不支持时自动退回铺满视口（与工位总览共用同一份实现） */
import { toggleFullscreen, onFullscreenChange } from './js/fullscreen.js';

/* ============================================================
   工位详情看板（气体）· A区（流程区）
   - 入口：工位总览（StationViewGas）点击同一列相邻的两张桌子 → /stationInfoGas?pair=N
   - 数据：SSE 实时推送 getStationListSse（GET api/dash/cockpit/station/sse，
     鉴权 token 走 Query params），打开页面就订阅，后端每 5 秒推一次本区最新工位信息；
     首帧为尽快出画仍用 getStationList（processCode = 'A'）拉一次全量；
     后端一行 = 一个工位（位置信息 + 绑定的在制产品信息），收到就整盘替换
   - 一屏展示该单元两张桌子的全部工位（工位数由后端数据决定）
   - 工位排布与总览一一对应：一张桌一行，行内按位号横排该桌工位
   - 点击任意工位弹出详情：工位自身信息 + 所绑产品信息，
     后端没传 / 值为空的字段整项不显示（不显示占位「—」）
   - 工位颜色 = 是否绑有产品 + 产品执行状态（未绑灰 / 正常绿 / 暂停黄 / 停工红）
   - 弹层右上「产品详情」：按产品编号调 getProductInfoList（Type: 'ProductInformation'），
     弹出「部件 / 自检确认 / 自检记录 / 问题」四表格，和产品列表页点产品编号完全一致
   - 工位列表接口带回 isIssue（"true" / "false"）→ 有问题的产品卡片打「!」标记
   ============================================================ */

const TICKER_SPEED = 55; // 告警滚动速度 px/s

/* ============ 「完整信息」浮层提示（鼠标能悬浮，触屏能点） ============
   原来内容被省略号截断时靠原生 title 悬浮看完整内容，但现场是触摸屏，
   title 在触屏上点不出来，所以自己画一个浮层，两种操作都支持：
   - 鼠标：移入弹出、移出收起（和原来一样，内容即时显示，不用等 1 秒）
   - 点击 / 触屏：点一下固定显示，再点一下关掉；点别处、滚屏、Esc 也会关
   用法：v-tip="fullTip('字段', 值)"；值为空串（空值 / 占位「—」）时不弹。 */
const TIP_GAP = 8;      // 浮层与元素之间留的空隙
const TIP_MAX_W = 320;  // 浮层最大宽度（长内容自动换行）
let tipEl = null;       // 浮层节点（全局单例，挂 body 上）
let tipOwner = null;    // 当前正在给哪个元素显示提示
let tipPinned = false; // 是否处于「点击固定」状态（触屏点出来的）

function tipNode() {
  if (tipEl && document.body.contains(tipEl)) return tipEl;
  tipEl = document.createElement('div');
  tipEl.className = 'gas-tip';
  document.body.appendChild(tipEl);
  return tipEl;
}

// 定位：优先显示在元素正上方（不挡住元素本身），上面放不下就落到下面，左右贴边自动收进来
function tipPlace(target) {
  const el = tipNode();
  const r = target.getBoundingClientRect();
  const w = Math.min(el.offsetWidth || 0, window.innerWidth - TIP_GAP * 2);
  const h = el.offsetHeight || 0;
  let left = r.left + r.width / 2 - w / 2;
  left = Math.max(TIP_GAP, Math.min(left, window.innerWidth - w - TIP_GAP));
  let top = r.top - h - TIP_GAP;
  if (top < TIP_GAP) top = Math.min(r.bottom + TIP_GAP, window.innerHeight - h - TIP_GAP);
  el.style.left = `${Math.round(left)}px`;
  el.style.top = `${Math.max(TIP_GAP, Math.round(top))}px`;
}

function tipShow(target, text) {
  if (!text) return;
  const el = tipNode();
  el.style.maxWidth = `${TIP_MAX_W}px`;
  el.textContent = text;
  el.classList.add('on');
  tipOwner = target;
  tipPlace(target); // 内容先落地，再按尺寸定位
}

function tipHide(target) {
  if (!tipEl) return;
  if (target && tipOwner !== target) return; // 只关某个元素自己的提示，别误关别人的
  tipEl.classList.remove('on');
  tipOwner = null;
  tipPinned = false;
}

// 组件销毁时把挂在 body 上的浮层摘掉，别留在页面里
function tipDestroy() {
  if (tipEl && tipEl.parentNode) tipEl.parentNode.removeChild(tipEl);
  tipEl = null;
  tipOwner = null;
  tipPinned = false;
}

// 指令：一个元素同时支持「悬浮」和「点击 / 触屏点按」
const tipDirective = {
  bind(el, binding) {
    el.__tipText = binding.value || '';
    // 悬浮：鼠标移入弹出、移出收起（点击固定状态下不干扰）
    el.__tipEnter = () => { if (!tipPinned) tipShow(el, el.__tipText); };
    el.__tipLeave = () => { if (!tipPinned) tipHide(el); };
    // 点按：第一次点固定显示，再点一次关掉；stopPropagation 防止被「点别处关闭」立刻关掉
    el.__tipTap = (e) => {
      if (!el.__tipText) return;
      if (tipOwner === el && tipPinned) { tipHide(el); return; }
      tipPinned = true;
      tipShow(el, el.__tipText);
      e.stopPropagation();
    };
    el.addEventListener('mouseenter', el.__tipEnter);
    el.addEventListener('mouseleave', el.__tipLeave);
    el.addEventListener('click', el.__tipTap);
  },
  // 数据实时刷新（SSE 每 5 秒一条）：正显示着的内容跟着更新，变成空值就直接收起来
  update(el, binding) {
    el.__tipText = binding.value || '';
    if (tipOwner !== el) return;
    if (!el.__tipText) tipHide(el);
    else tipShow(el, el.__tipText);
  },
  unbind(el) {
    el.removeEventListener('mouseenter', el.__tipEnter);
    el.removeEventListener('mouseleave', el.__tipLeave);
    el.removeEventListener('click', el.__tipTap);
    if (tipOwner === el) tipHide(el);
  },
};

/* ============================================================
   卡片字段值超宽时的「行内缓缓滚动」
   ------------------------------------------------------------
   卡片横向空间有限（尤其 2 工位宽卡还要分两列），值一长就会被省略号截断。
   CSS 自己不知道内容到底超没超宽，所以这里量一下：
   超出宽度写进 CSS 变量 --kv-shift，只有真的超宽才挂动画类，
   短文本保持静止，不会无意义地来回晃。

   ⚠️ 滚动速度用「每秒多少像素」而不是「一轮多少秒」：
      MARQUEE_SPEED 是恒定速度，长文本多滚一会儿、短文本少滚一会儿，
      无论文字多长，眼睛看到的滚动快慢都是一样的。
      （若固定总时长，字多的会飞快、字少的会奇慢，看着很乱）
   ============================================================ */
/* 滚动速度：每秒滚过的像素数（想快就调大，想慢就调小）——所有字段都用这个速度 */
const MARQUEE_SPEED = 10;
/* 一轮首尾各停顿多久（秒）：固定值，不随文字长度变化
   ⚠️ 原来停顿是按百分比（12% / 88%）写在 @keyframes 里的，
      文字越长一轮总时长越大 → 开头要干等十几秒才动，看着就像「它不滚」。
      现在改用 Web Animations API 精确编排时间轴：停顿固定 0.8s，移动时间 = 距离 ÷ 速度。 */
const MARQUEE_HOLD = 0.8;

/** 停掉当前滚动（切换动画 / 卸载组件时都要清干净） */
function marqueeStop(el) {
  if (el.__mqAnim) { el.__mqAnim.cancel(); el.__mqAnim = null; }
  el.classList.remove('is-marquee');
}

/** 按「固定停顿 + 恒定速度」启动一轮循环滚动 */
function marqueeRun(el, inner, over) {
  marqueeStop(el);
  const travel = over / MARQUEE_SPEED;                     // 移动耗时 = 距离 ÷ 恒定速度
  const total = MARQUEE_HOLD + travel + MARQUEE_HOLD;      // 一轮总时长
  el.style.setProperty('--kv-shift', `${over}px`);
  el.style.setProperty('--kv-dur', `${total.toFixed(2)}s`);

  // 首选 Web Animations API：可以自定义每个关键点的 offset，停顿时间才是固定的
  if (typeof inner.animate === 'function') {
    const p1 = MARQUEE_HOLD / total;                       // 开头停顿结束
    const p2 = (MARQUEE_HOLD + travel) / total;            // 滚动结束，末尾停顿开始
    el.__mqAnim = inner.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(0)', offset: p1 },
        { transform: `translateX(${-over}px)`, offset: p2 },
        { transform: `translateX(${-over}px)` },
      ],
      { duration: total * 1000, iterations: Infinity }
    );
    return;
  }
  // 老浏览器没有 element.animate → 退回 CSS @keyframes（12% / 88% 的近似停顿）
  requestAnimationFrame(() => el.classList.add('is-marquee'));
}

function marqueeMeasure(el, force) {
  const inner = el.firstElementChild;
  if (!inner) return;

  // 还没布局出来（卡片刚插入 / 处于隐藏状态）：下一帧再量，最多试几帧
  const boxW = el.clientWidth;
  if (!boxW) {
    el.__mqTry = (el.__mqTry || 0) + 1;
    if (el.__mqTry < 5) requestAnimationFrame(() => marqueeMeasure(el, force));
    return;
  }
  el.__mqTry = 0;

  // transform 不影响这两个宽度，所以不用先停动画再量
  // scrollWidth 在个别浏览器下不含字母间距，用实际渲染宽度兜底，取两者较大值
  const textW = Math.max(inner.scrollWidth, inner.getBoundingClientRect().width);
  const over = Math.round(textW - boxW);
  const text = inner.textContent || '';

  // 内容 / 超出量都没变，且动画正在跑 → 别打断（SSE 每 5 秒刷一次会频繁触发 update）
  const running = !!el.__mqAnim || el.classList.contains('is-marquee');
  if (!force && el.__mqOver === over && el.__mqText === text && (running || over <= 2)) return;

  el.__mqOver = over;
  el.__mqText = text;
  if (over > 2) marqueeRun(el, inner, over);
  else marqueeStop(el);
}

/* 卡片宽度是 grid 现算的，首帧可能还没稳定 → 用 ResizeObserver 盯着，尺寸一变就重测 */
const mqObserver = (typeof ResizeObserver !== 'undefined')
  ? new ResizeObserver((entries) => { entries.forEach(e => marqueeMeasure(e.target, true)); })
  : null;

const marqueeDirective = {
  inserted(el) {
    marqueeMeasure(el, true);
    if (mqObserver) {
      mqObserver.observe(el);
      const inner = el.firstElementChild;
      if (inner) mqObserver.observe(inner);
    }
  },
  update: el => marqueeMeasure(el, false),
  componentUpdated: el => marqueeMeasure(el, false),
  unbind(el) {
    marqueeStop(el);
    if (mqObserver) {
      mqObserver.unobserve(el);
      const inner = el.firstElementChild;
      if (inner) mqObserver.unobserve(inner);
    }
  },
};

export default {
  name: 'StationInfoGas',
  components: { ProductPartInfoDialog },
  directives: { tip: tipDirective, marquee: marqueeDirective },
  data() {
    return {
      now: '--:--:--',
      dateText: '----年--月--日',
      shift: '白班',
      zone: ZONE_CODE,  // 当前看板对应的区（processCode）
      pairs: [],        // 全部下钻单元（同一列相邻 2 桌 / 工位 + 实时状态）
      doneByUnit: {},   // 各单元完工数（本组在制产品里「产品进程 = 完工」的数量）
      alertsByUnit: {}, // 各单元实时告警
      tickerItems: [],
      loading: false,   // 接口请求中
      loadErr: '',      // 接口失败提示
      sse: null,        // SSE 连接（EventSource），离开页面要 .close()
      sseErr: '',       // SSE 断流提示（EventSource 会自动重连，恢复后置空）
      lastBoardAt: 0,   // 当前显示这批数据的时刻：更旧的数据不再回写覆盖
      prevStatus: null, // 上一次本组各工位状态（用来比对本次刷新有哪些变了）
      active: null,     // 弹层中的工位 { st, table, row }
      showMap: false,   // 顶部「总览位置选择」面板是否展开

      // 产品详情弹窗（部件 / 自检确认 / 自检记录 / 问题 四表格）
      partsDialogVisible: false,
      partsList: [],
      inspectionList: [],
      resInspectionList: [],
      partsIssueList: [],
      currentPartsProductCode: '',
      partsLoading: false,
      fs: false, // 是否处于全屏（按钮图标 / 提示跟着切）
    };
  },
  computed: {
    // 当前下钻单元：来自地址栏 ?pair=N（默认第 1 个），总览点击某列两桌后跳到本页即按此取数
    pairIndex() {
      const n = Number.parseInt(this.$route.query.pair, 10);
      if (Number.isNaN(n)) return 0;
      return Math.min(Math.max(n, 0), Math.max(this.pairs.length - 1, 0));
    },
    pair() {
      return this.pairs[this.pairIndex] || { index: 0, code: '', rows: [], tables: [], thin: false, stationCount: 0 };
    },
    statusTxt() { return STATUS_TXT; },
    // 本单元（两桌）统计
    stats() {
      const c = { total: 0, ok: 0, warn: 0, err: 0, idle: 0 };
      this.pair.tables.forEach((t) => t.stations.forEach((s) => {
        c.total += 1;
        if (s.status === 'normal') c.ok += 1;
        else if (s.status === 'warning') c.warn += 1;
        else if (s.status === 'error') c.err += 1;
        else c.idle += 1;
      }));
      return c;
    },
    doneTotal() { return this.doneByUnit[this.pairIndex] || 0; },
    alerts() { return this.alertsByUnit[this.pairIndex] || []; },
    // 布局说明：一张桌一行 → 行数 = 桌子数、列数 = 每桌工位数（普通 2 行 × 6 列 / 第 7-10 行 2 行 × 2 列）
    layoutText() {
      const tables = this.pair.tables;
      if (!tables.length || !tables[0].stations.length) return '';
      const per = tables[0].stations.length;
      const total = tables.reduce((n, t) => n + t.stations.length, 0);
      return `${tables.length} 行 × ${per} 列 · 共 ${total} 个工位`;
    },
    activeId() { return this.active ? this.active.st.id : ''; },
    // 弹层：同列的另一张桌子（本单元就是这两桌）
    peerCode() {
      if (!this.active) return '';
      const peer = this.pair.tables.find((t) => t.code !== this.active.table.code);
      return peer ? peer.code : '';
    },
    /**
     * 弹层字段：工位自身信息 + 所绑产品的信息（与接口字段一一对应）
     * 规则：后端没发这个字段 / 值为空 → 整项不显示（不渲染占位「—」），
     *      所以不同工位看到的项数可能不一样
     * 注：product_status（未用状态字段）、process_code（区）按约定不展示
     */
    detailFields() {
      const a = this.active;
      if (!a) return [];
      const st = a.st;
      const p = st.product; // 未绑产品 → 产品相关项全部为空，自然都不显示
      const raw = [
        /* ---- 工位自身 ---- */
        { label: '工位名称', value: st.name },
        { label: '所在桌子', value: a.table.code },
        { label: '工位状态', value: st.stationStatus },
        { label: '工位类型', value: st.type },
        { label: '负责人', value: st.operator },
        { label: '工位备注', value: st.remark },
        /* ---- 绑定的产品 ---- */
        { label: '产品ID', value: p ? p.id : '' },
        { label: '产品名称', value: p ? p.name : '' },
        { label: '产品编号', value: p ? p.serialCode : '' },
        { label: '产品型号', value: p ? p.model : '' },
        { label: '短代码', value: p ? p.fsn : '' },
        { label: '任务单号', value: p ? p.taskNumber : '' },
        { label: '合同号', value: p ? p.contract : '' },
        { label: '合同编号', value: p ? p.contractNo : '' },   // ✅ contract_no，空则整项不显示
        { label: '最终业主', value: p ? p.owner : '' },
        { label: '量程', value: p ? p.range : '' },
        { label: '设计人', value: p ? p.designer : '' },
        { label: '回复交货期', value: p ? p.replyDate : '' },
        { label: '预计交付时间', value: p ? p.planFinishDate : '' },
        { label: '产品进程', value: p ? processText(p.processStatus) : '' },
        { label: '产品执行状态', value: p ? executeText(p.executeStatus) : '' },
        { label: '有无问题', value: p ? (p.isIssue ? '有' : '无') : '' },
        { label: '创建人', value: p ? p.createBy : '' },
        { label: '创建时间', value: p ? p.createTime : '' },
        { label: '更新人', value: p ? p.updateBy : '' },
        { label: '更新时间', value: p ? p.updateTime : '' },
        { label: '开始时间', value: p ? p.startTime : '' },
        { label: '调试参数', value: p ? p.parameter : '' },
        { label: '备注', value: p ? p.detail : '' },
        { label: '小备注', value: p ? p.remark : '' },
        { label: '初始编号', value: p ? p.serialCodeOriginal : '' },
        { label: '历史编号', value: p ? p.serialCodeHistory : '' },
      ];
      // 只留下有内容的项：空串 / null / undefined / 取不到文案的「—」都丢掉
      return raw.filter(f =>
        f.value !== undefined && f.value !== null && f.value !== '' && f.value !== '—'
      );
    },
  },
  watch: {
    /* 切换下钻单元：关掉弹层与位置面板、换该单元的告警流 */
    pairIndex() {
      this.closeStation();
      this.showMap = false;
      this.prevStatus = null; // 换组后重新记状态基线（不拿上一组的状态做比对）
      if (!this.alertsByUnit[this.pairIndex]) this.seedAlerts(this.pairIndex);
      this.renderTicker();
    },
  },
  methods: {
    /** ✅ 窗口尺寸变化后重新测量所有卡片字段值（防抖 150ms，避免 resize 期间反复计算） */
    onMarqueeResize() {
      if (this.marqueeTimer) clearTimeout(this.marqueeTimer);
      this.marqueeTimer = setTimeout(() => {
        const els = this.$el ? this.$el.querySelectorAll('.dw-kv .kv-val') : [];
        els.forEach((el) => marqueeMeasure(el, true));   // 宽度变了 → 强制重测
      }, 150);
    },

    /* ---------- 导航 ---------- */
    backHome() {
      // 返回主页：按权限决定默认主页，避免跳转到无权限的项目页
      this.$router.push(UserInfoUtils.getDefaultHomeRoute());
    },
    goOverview() {
      // 返回上一级：工位总览（气体）
      this.$router.push('/stationViewGas');
    },
    /* ---------- 全屏 ---------- */
    // 整个看板铺满屏幕：优先原生全屏，不支持时自动退回「铺满视口」
    toggleFs() {
      toggleFullscreen(this.$el, (on) => { this.fs = on; });
    },
    /* ---------- 桌子统计 ---------- */
    busyCount(t) {
      let n = 0;
      t.stations.forEach((s) => { if (s.status !== 'idle') n += 1; });
      return n;
    },
    // 桌子整体状态：有异常 → 异常，有预警 → 预警，全部空闲 → 空闲，否则正常
    tableState(t) {
      if (t.stations.some((s) => s.status === 'error')) return 'error';
      if (t.stations.some((s) => s.status === 'warning')) return 'warning';
      if (t.stations.every((s) => s.status === 'idle')) return 'idle';
      return 'normal';
    },
    stateTxt(st) {
      if (!st.product) return '未绑定产品';
      if (st.status === 'normal') return '正常作业中';
      return st.status === 'error' ? '停工待处理' : '暂停作业中';
    },
    // 产品进程 / 产品执行状态文案（卡片与详情弹层用）
    processText(v) { return processText(v); },
    executeText(v) { return executeText(v); },
    // 该工位所绑产品是否有问题：工位列表接口带回 isIssue（"true"/"false" 或布尔），
    // 数据层已统一转成布尔，具体的问题内容由 getProductInfoList 现查
    hasIssue(st) {
      return !!(st.product && st.product.isIssue);
    },
    /* 悬浮提示：卡片 / 弹窗里内容过长会被省略号截断，鼠标悬浮时用原生 tooltip 显示完整内容。
       格式统一「字段名：值」；空值 / 占位「—」不提示，免得悬浮弹出没意义的框 */
    fullTip(label, val) {
      const v = (val === undefined || val === null) ? '' : String(val);
      if (!v || v === '—') return '';
      return `${label}：${v}`;
    },
    // 产品进程 → 进度条百分比改了变得更细了：下达0 / 装配中25 / 调试中50 / 检验中75 / 出库100 / 完工100
    progressPct(st) {
      if (!st.product) return 0;
      const map = { 1: 1, 10:5, 12:10, 13:15, 2: 25, 3: 50, 4: 75, 9:90, 11: 95 , 5: 100 };
      const v = map[st.product.processStatus];
      return v == null ? 0 : v;
    },
    // 底部提示文字：正常卡 = 进程名；暂停 / 停工卡 = 原因；空闲卡 = 占位
    noteText(st) {
      if (!st.product) return '工位空闲，未绑定产品';
      if (st.status === 'normal') return processText(st.product.processStatus);
      return st.note || (st.status === 'error' ? '停工待处理' : '暂停作业中');
    },
    /* ---------- 工位详情弹层 ---------- */
    openStation(st, table) { this.hideTip(); this.active = { st, table, row: table.row }; },
    closeStation() { this.active = null; this.hideTip(); },

    /* ---------- 「完整信息」浮层提示 ---------- */
    // 收起浮层（点空白 / 滚屏 / 缩放 / 关弹层 / Esc 时都走这里）；返回刚才是否有浮层被收起
    hideTip() {
      const had = !!tipOwner;
      tipHide();
      return had;
    },
    // 点页面任意空白处：关掉点击固定状态的浮层（浮层自身不接收点击，pointer-events: none）
    onBlankTouch() { this.hideTip(); },

    /* ---------- 产品详情：部件 / 自检确认 / 自检记录 / 问题 ----------
       与产品列表页 productProgress.handleSerialCodeClick 完全一致：
       按产品编号调 getProductInfoList（Type: 'ProductInformation'），
       拿到 parts / inspectionList / resInspectionList / issue 四个数组后打开同一个弹窗组件。
       工位列表接口只告诉「有没有问题」（isIssue），具体问题内容由这里现查。 */
    async openProductInfo(st) {
      const serialCode = st && st.product ? st.product.serialCode : '';
      if (!serialCode || this.partsLoading) return;
      this.partsLoading = true;
      try {
        const res = await getProductInfoList({ Type: 'ProductInformation', msg: serialCode });
        if (res.data.code === 200) {
          const data = res.data.data || {};
          const parts = data.parts || [];
          const inspection = data.inspectionList || [];
          const resInspection = data.resInspectionList || [];
          // 后端无问题时 issue 可能是字符串 '空'，做兼容处理
          const issue = Array.isArray(data.issue) ? data.issue : [];
          if (parts.length || inspection.length || resInspection.length || issue.length) {
            this.partsList = parts;
            this.inspectionList = inspection;
            this.resInspectionList = resInspection;
            this.partsIssueList = issue;
            this.currentPartsProductCode = serialCode;
            this.partsDialogVisible = true;
          } else {
            this.$message.info('该产品没有详细信息');
          }
        } else {
          this.$message.error('查询产品信息失败：' + res.data.message);
        }
      } catch (error) {
        this.$message.error('查询产品信息失败');
      } finally {
        this.partsLoading = false;
      }
    },
    closePartsDialog() {
      this.partsDialogVisible = false;
      this.partsList = [];
      this.inspectionList = [];
      this.resInspectionList = [];
      this.partsIssueList = [];
      this.currentPartsProductCode = '';
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        // 「完整信息」浮层开着就只关浮层，别顺手把工位详情也关了
        if (this.hideTip()) return;
        // 全屏时 ESC 优先退出全屏：浏览器自己退出原生全屏，兜底伪全屏在这里手动退出，
        // 不再顺手关掉工位弹层，避免「只想退出全屏却把详情也关了」
        if (this.fs) {
          if (!document.fullscreenElement && !document.webkitFullscreenElement) this.toggleFs();
          return;
        }
        this.closeStation();
        this.showMap = false;
      }
    },

    /* ---------- 总览位置选择：在迷你排布图上直接选组（同一列上下两张桌为一组） ---------- */
    toggleMap() { this.showMap = !this.showMap; },
    // 组整体状态（迷你图格子上色）：有异常 → 异常，有预警 → 预警，全空闲 → 空闲，否则正常
    pairState(p) {
      let idle = 0;
      let warn = false;
      for (let i = 0; i < p.tables.length; i += 1) {
        const list = p.tables[i].stations;
        for (let j = 0; j < list.length; j += 1) {
          const s = list[j].status;
          if (s === 'error') return 'error';
          if (s === 'warning') warn = true;
          else if (s === 'idle') idle += 1;
        }
      }
      if (warn) return 'warning';
      return idle === p.stationCount ? 'idle' : 'normal';
    },
    // 切到某一组：只改「看哪一组」，用 replace 不往历史堆记录，返回时仍回到工位总览
    goPair(i) {
      this.showMap = false;
      if (i === this.pairIndex) return;
      this.$router.replace({ path: '/stationInfoGas', query: { pair: String(i) } });
    },

    /* ---------- 数据刷新：轮询真实接口 getStationList ---------- */
    // 本组完工数：接口没给完工流水，按「本组在制产品里进程 = 完工(9)」的数量显示
    countDone(p) {
      let n = 0;
      p.tables.forEach((t) => t.stations.forEach((s) => {
        if (s.product && s.product.processStatus === 9) n += 1;
      }));
      return n;
    },
    // 告警严重度：跟着工位状态走（接口只给状态，不给原因）
    sevOf(status) {
      if (status === 'error') return 'error';
      if (status === 'warning') return 'warning';
      return 'info';
    },
    // 本次刷新本组有哪些工位状态变了 → 补进告警流
    diffAlerts() {
      const cur = {};
      this.pair.tables.forEach((t) => t.stations.forEach((s) => { cur[s.id] = s.status; }));
      const prev = this.prevStatus;
      this.prevStatus = cur;
      if (!prev) return []; // 首次只记基线
      const changes = [];
      Object.keys(cur).forEach((id) => {
        const before = prev[id];
        if (before && before !== cur[id]) {
          changes.push({
            id,
            sev: this.sevOf(cur[id]),
            text: `执行状态：${STATUS_TXT[before] || before} → ${STATUS_TXT[cur[id]] || cur[id]}`,
          });
        }
      });
      return changes;
    },
    // 工位数据落到界面：首帧 REST 和 SSE 推来的都走这里
    // at 是数据的「出生时刻」，比当前显示的还旧就丢掉，防止慢请求把新数据写回去
    applyBoard(board, isRefresh, at) {
      if (at && at < this.lastBoardAt) return false;
      if (at) this.lastBoardAt = at;
      this.pairs = board.pairs;
      this.loadErr = '';
      this.sseErr = '';
      this.pairs.forEach((p, pi) => {
        this.$set(this.doneByUnit, pi, this.countDone(p));
        // 首次（或该组还没有告警流）时铺一条初始告警；刷新时保留已有告警，只补变化
        if (!this.alertsByUnit[pi]) this.seedAlerts(pi);
      });
      const changes = this.diffAlerts();
      if (isRefresh) {
        changes.slice(0, 4).forEach((c) => this.pushAlert(c.sev, `工位 ${c.id} ${c.text}`));
      } else {
        this.renderTicker();
      }
      return true;
    },
    async loadBoard(isRefresh) {
      const at = Date.now();
      if (this.loading) return;
      this.loading = true;
      try {
        const board = await fetchBoard(this.zone); // processCode = 当前区
        this.applyBoard(board, isRefresh, at);
      } catch (e) {
        this.loadErr = '工位数据加载失败：' + (e.message || '网络错误');
      } finally {
        this.loading = false;
      }
    },
    /* 打开页面就订阅 SSE：后端每 5 秒推本区最新工位信息，收到就整盘替换 */
    openStream() {
      this.closeStream();
      this.sse = openStationStream(this.zone, {
        onBoard: (board) => {
          this.sseErr = '';
          this.applyBoard(board, !!this.lastBoardAt, Date.now());
        },
        // EventSource 自带重连，这里只负责把状态显示出来
        onError: () => { this.sseErr = '实时推送连接中断，正在自动重连…'; },
      });
    },
    closeStream() {
      if (this.sse) { this.sse.close(); this.sse = null; }
    },

    /* ---------- 告警滚动条 ---------- */
    pushAlert(sev, text) {
      const list = this.alertsByUnit[this.pairIndex];
      if (!list) return;
      list.unshift({ sev, time: nowTime(), text });
      if (list.length > 10) list.pop();
      this.renderTicker();
    },
    renderTicker() {
      const list = this.alerts;
      if (!list.length) { this.tickerItems = []; return; }
      const half = [];
      for (let i = 0; i < 3; i++) half.push(...list);
      this.tickerItems = [...half, ...half]; // 双份内容实现无缝循环
      this.$nextTick(() => {
        const track = this.$refs.tickerTrack;
        if (!track) return;
        const w = track.scrollWidth / 2;
        track.style.animationDuration = Math.max(30, Math.round(w / TICKER_SPEED)) + 's';
      });
    },
    // 用该单元真实的预警 / 异常工位铺一条初始告警流（接口不返回原因，只显示状态）
    seedAlerts(pi) {
      const p = this.pairs[pi];
      if (!p) return;
      const errs = [];
      const warns = [];
      let idle = 0;
      let busy = 0;
      p.tables.forEach((t) => t.stations.forEach((st) => {
        if (st.status === 'error') errs.push(st);
        else if (st.status === 'warning') warns.push(st);
        else if (st.status === 'idle') idle += 1;
        else busy += 1;
      }));
      const list = [];
      errs.slice(0, 3).forEach((st) => list.push({ sev: 'error', time: nowTime(), text: `工位 ${st.id} ${st.model || st.stationStatus || ''} 执行状态：停工` }));
      warns.slice(0, 2).forEach((st) => list.push({ sev: 'warning', time: nowTime(), text: `工位 ${st.id} ${st.model || st.stationStatus || ''} 执行状态：暂停` }));
      list.push({ sev: 'done', time: nowTime(), text: `${p.code} 在制工位 ${busy} 个，其中已完工 ${this.doneByUnit[pi] || 0} 台` });
      list.push({ sev: 'info', time: nowTime(), text: `${p.code} 空闲工位 ${idle} 个，等待上件` });
      this.$set(this.alertsByUnit, pi, list);
    },

    /* ---------- 时钟 ---------- */
    tickClock() {
      const d = new Date();
      this.now = nowTime();
      const week = '日一二三四五六'[d.getDay()];
      this.dateText = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} 星期${week}`;
      const h = d.getHours();
      this.shift = (h >= 8 && h < 20) ? '白班' : '夜班';
    },

    /* ---------- 初始化 ---------- */
    init() {
      this.tickClock();
      this.loadBoard(false); // 首帧：REST 拉一次全量（与总览同一接口 / 同一口径）
      this.openStream();     // 之后全由 SSE 推：后端每 5 秒一条，不再轮询
      this.timerClock = setInterval(this.tickClock, 1000);
      window.addEventListener('keydown', this.onKeydown);
      // 「完整信息」浮层：点空白 / 滚屏 / 缩放时收起（浮层是 body 上的单例，只能挂全局监听）
      document.addEventListener('click', this.onBlankTouch);
      document.addEventListener('scroll', this.onBlankTouch, true);
      window.addEventListener('resize', this.onBlankTouch);
    },
  },
  mounted() {
    this.init();
    // 原生全屏按 ESC / F11 退出时不经过按钮，靠这个把按钮图标同步回来
    this.offFsChange = onFullscreenChange((on) => { this.fs = on; });
    // 窗口尺寸变了（含进出全屏）卡片宽度也变 → 重新量一次哪些字段需要滚动
    window.addEventListener('resize', this.onMarqueeResize);
  },
  beforeDestroy() {
    this.closeStream();   // 离开关掉 SSE 长连接
    clearInterval(this.timerClock);
    window.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onBlankTouch);
    document.removeEventListener('scroll', this.onBlankTouch, true);
    window.removeEventListener('resize', this.onBlankTouch);
    window.removeEventListener('resize', this.onMarqueeResize);
    if (this.marqueeTimer) clearTimeout(this.marqueeTimer);
    tipDestroy(); // 摘掉挂在 body 上的「完整信息」浮层
    if (this.offFsChange) this.offFsChange(); // 摘掉全屏状态监听
  },
};
</script>

<style scoped>
/* ================= 暗色大屏主题 =================
   注意：组件使用 scoped 样式，CSS 变量必须定义在组件根元素 .gas-detail 上
   （若定义在 :root 上，scoped 编译后不生效，导致 var(--xx) 全部失效） */
.gas-detail {
  /* 配色对齐 overallDashboard：深蓝底 + 亮青主色；正文/数值用实色高对比（透明度压到很低） */
  --bg: #072143;                    /* 深蓝底（兜底色，实际用下面的渐变） */
  --panel: #072244;                 /* 内容面板：纯色实底 */
  --card: #0a2b52;                  /* 卡片底色：纯色实底，卡片里所有混色都以它为底 */
  --line: rgba(0, 174, 255, 0.38);
  --line-soft: rgba(0, 174, 255, 0.18);
  --txt: #ffffff;                   /* 主文字：纯白，强对比 */
  --txt-dim: #8eb5c9;               /* 次要文字：亮钢蓝（不再用暗灰） */
  --ok: #1dd183;    /* 正常 · 绿 */
  --warn: #feca57;  /* 预警 · 黄 */
  --err: #ff6b6b;   /* 异常 · 红 */
  --idle: #9aa7b8;  /* 未绑产品 · 灰 */
  --accent: #00aeff;
  --accent-2: #00c3ff;
  /* 全站统一字体：微软雅黑（数字、编号、标题都走这一套） */
  --font-num: "Microsoft YaHei", "PingFang SC", "Noto Sans SC", sans-serif;

  position: relative;
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  /* 纯色深蓝底：不要网格、不要光晕、不要渐变 */
  background: var(--bg);
  color: var(--txt);
  font-family: "Microsoft YaHei", "PingFang SC", "Noto Sans SC", sans-serif;
  -webkit-font-smoothing: antialiased;
  user-select: none;
}
.gas-detail, .gas-detail * { margin: 0; padding: 0; box-sizing: border-box; }
.gas-detail ::selection { background: rgba(0, 174, 255, .35); }

/* ---------- 舞台 ---------- */
.gas-stage {
  flex: 1; min-width: 0; min-height: 0;
  width: 100%; height: 100%;
  display: flex; flex-direction: column;
  padding: 16px 30px 14px; gap: 12px;
}
.panel {
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 10px;
}

/* ---------- 顶部信息栏 ---------- */
.topbar {
  flex: 0 0 72px;
  display: flex; align-items: center;
  padding: 0 18px; gap: 14px;
  /* 不能 overflow: hidden —— 位置选择面板要垂到顶栏下方 */
  position: relative;
}
.topbar::after {
  content: ""; position: absolute; left: 0; right: 0; bottom: 0; height: 2px;
  border-radius: 0 0 10px 10px;
  background: rgba(0, 174, 255, .5);
}

.brand { display: flex; align-items: center; gap: 12px; min-width: 0; }
.logo {
  width: 40px; height: 40px; border-radius: 10px; flex: 0 0 auto;
  border: 1px solid rgba(0, 174, 255, .55);
  background: rgba(5, 149, 235, .18);
  display: grid; place-items: center;
}
.logo svg { width: 26px; height: 26px; }
/* 标题：亮青实色字 */
.brand-txt h1 {
  font-size: 22px; font-weight: 700; letter-spacing: 3px;
  color: var(--accent-2);
  white-space: nowrap;
}
.brand-txt p { margin-top: 2px; font-size: 10px; letter-spacing: 2.5px; color: var(--txt-dim); white-space: nowrap; }
.mode-tag {
  margin-left: 6px; padding: 4px 12px;
  font-size: 11.5px; color: var(--accent-2);
  border: 1px solid rgba(0, 174, 255, .5);
  background: rgba(5, 149, 235, .2);
  border-radius: 999px; white-space: nowrap;
}

/* 核心指标 */
.metrics { display: flex; margin-left: auto; }
.metric { padding: 0 15px; border-left: 1px solid rgba(0, 174, 255, .28); display: flex; flex-direction: column; gap: 3px; }
.m-label { font-size: 10.5px; color: var(--txt-dim); letter-spacing: 1px; white-space: nowrap; }
.m-value b { font-family: var(--font-num); font-size: 24px; font-weight: 700; color: #ffffff; }
.m-value small { font-size: 11px; color: var(--txt-dim); margin-left: 3px; font-weight: 400; }
.m-value.c-cyan b { color: var(--accent); }
.m-value.c-green b { color: var(--ok); }
.m-value.c-warn b { color: var(--warn); }
.m-value.c-red b { color: var(--err); }

/* 时钟 */
.clock-box { padding-left: 15px; border-left: 1px solid rgba(0, 174, 255, .28); text-align: right; }
.clock-time { font-family: var(--font-num); font-size: 24px; font-weight: 700; letter-spacing: 1px; color: #ffffff; white-space: nowrap; }
.clock-sub { margin-top: 2px; font-size: 10.5px; color: var(--txt-dim); display: flex; align-items: center; justify-content: flex-end; gap: 8px; white-space: nowrap; }
.shift { padding: 1px 9px; border-radius: 999px; font-size: 10px; color: var(--accent-2); border: 1px solid rgba(0, 174, 255, .5); background: rgba(5, 149, 235, .2); }

/* 返回按钮：总览（带文字）+ 主页（仅小房子） */
.back-link {
  flex: 0 0 auto;
  display: flex; align-items: center; gap: 4px;
  height: 30px; padding: 0 11px 0 7px;
  font-family: inherit; font-size: 11.5px; letter-spacing: .5px;
  color: #cdddf7; background: rgba(5, 149, 235, .16);
  border: 1px solid rgba(0, 174, 255, .45); border-radius: 8px;
  cursor: pointer; transition: color .15s, background .15s, border-color .15s, transform .15s;
}
.back-link svg { width: 15px; height: 15px; }
.back-link:hover { color: #ffffff; border-color: var(--accent-2); background: rgba(0, 174, 255, .32); transform: translateY(-1px); }
.back-link:active { transform: translateY(0); }
.back-btn {
  flex: 0 0 auto;
  width: 30px; height: 30px;
  display: grid; place-items: center;
  padding: 0; border: 0; border-radius: 8px;
  color: var(--accent); background: transparent;
  cursor: pointer; font-family: inherit;
  transition: color .15s, background .15s, transform .15s;
}
.back-btn svg { width: 18px; height: 18px; }
.back-btn:hover { color: #ffffff; background: rgba(0, 174, 255, .32); transform: translateY(-1px); }
.back-btn:active { transform: translateY(0); }

/* 全屏按钮：与「返回主页」同尺寸同观感，放在返回按钮左边 */
.fs-btn {
  flex: 0 0 auto;
  width: 30px; height: 30px;
  display: grid; place-items: center;
  padding: 0; border: 0; border-radius: 8px;
  color: var(--accent); background: transparent;
  cursor: pointer; font-family: inherit;
  transition: color .15s, background .15s, transform .15s;
}
.fs-btn svg { width: 18px; height: 18px; }
.fs-btn:hover { color: #ffffff; background: rgba(0, 174, 255, .32); transform: translateY(-1px); }
.fs-btn:active { transform: translateY(0); }
/* 原生全屏 / 兜底伪全屏时都要铺满整个屏幕 */
.gas-detail:fullscreen { width: 100vw; height: 100vh; background: var(--bg); }
.gas-detail.is-fs {
  position: fixed !important; inset: 0; z-index: 1200;
  width: 100vw; height: 100vh; background: var(--bg);
}

/* ---------- 总览位置选择（顶栏图标 + 迷你排布图） ---------- */
.map-sel { position: relative; flex: 0 0 auto; }
.map-btn {
  width: 30px; height: 30px;
  display: grid; place-items: center;
  padding: 0; border: 1px solid var(--line); border-radius: 8px;
  color: #cdddf7; background: rgba(5, 149, 235, .16);
  cursor: pointer; font-family: inherit;
  transition: color .15s, background .15s, border-color .15s, transform .15s;
}
.map-btn svg { width: 17px; height: 17px; fill: none; stroke: currentColor; stroke-width: 1.7; }
.map-btn:hover, .map-sel.open .map-btn { color: #ffffff; border-color: var(--accent-2); background: rgba(0, 174, 255, .32); }
.map-btn:active { transform: translateY(1px); }

/* top 里的 23px：按钮在 72px 顶栏里垂直居中（(72-30)/2=21），让面板紧贴顶栏下沿 */
.map-panel {
  position: absolute; top: calc(100% + 23px); right: 0; z-index: 40;
  width: 172px; padding: 10px 10px 8px;
  border: 1px solid rgba(0, 174, 255, .45); border-radius: 10px;
  background: var(--panel);
  box-shadow: 0 18px 40px rgba(0, 0, 0, .5), 0 0 0 1px rgba(0, 174, 255, .18);
  animation: mpIn .14s ease-out;
}
@keyframes mpIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
.mp-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 8px; }
.mp-title { font-size: 11.5px; letter-spacing: 1px; color: #ffffff; }
.mp-no { font-family: var(--font-num); font-size: 10.5px; color: var(--accent-2); }
/* 4 列 = 总览的 4 列桌子；7 行 = 总览 14 行每 2 行合并成的一组 */
.mp-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; }
.mp-cell {
  --mc: var(--idle);
  height: 26px; padding: 0;
  /* 色块加实：状态色占比拉高，深蓝底上一眼分辨 */
  border: 1px solid color-mix(in srgb, var(--mc) 60%, transparent);
  border-radius: 5px;
  background: color-mix(in srgb, var(--mc) 26%, var(--panel));
  cursor: pointer;
  transition: border-color .15s, background .15s, box-shadow .15s, transform .15s;
}
.mp-cell.st-normal { --mc: var(--ok); }
.mp-cell.st-warning { --mc: var(--warn); }
.mp-cell.st-error { --mc: var(--err); }
.mp-cell:hover { border-color: var(--mc); background: color-mix(in srgb, var(--mc) 46%, var(--panel)); }
.mp-cell.on { border-color: var(--accent-2); background: color-mix(in srgb, #00aeff 30%, var(--panel)); box-shadow: 0 0 0 1px var(--accent-2); }
.mp-foot {
  margin-top: 8px; text-align: center;
  font-family: var(--font-num); font-size: 10.5px; color: var(--txt-dim);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* ---------- 组详情看板 ---------- */
.board {
  flex: 1; min-height: 0;
  display: flex; flex-direction: column;
  padding: 8px 14px 12px;
}
.board-head { flex: 0 0 30px; display: flex; align-items: center; justify-content: space-between; gap: 12px; min-width: 0; }
.b-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
.z-mark { width: 4px; height: 16px; border-radius: 2px; flex: 0 0 auto; background: var(--accent-2); }
.board-head h2 { font-size: 15px; font-weight: 700; letter-spacing: 2px; color: #ffffff; white-space: nowrap; }
.z-code {
  font-family: var(--font-num); font-size: 9.5px; letter-spacing: 1.5px; color: var(--txt-dim);
  padding: 1px 8px; border: 1px solid var(--line); border-radius: 4px; white-space: nowrap;
}
.z-meta { font-size: 11px; color: var(--txt-dim); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.b-right { display: flex; align-items: center; gap: 6px; flex: 0 1 auto; min-width: 0; }
.hint { font-size: 10.5px; color: var(--txt-dim); white-space: nowrap; margin-right: 2px; }
.rc {
  font-style: normal; font-size: 10.5px; padding: 1px 9px; border-radius: 999px;
  border: 1px solid; white-space: nowrap; display: flex; align-items: center; gap: 4px;
}
.rc b { font-family: var(--font-num); font-size: 11.5px; font-weight: 700; }
.rc.c-cyan { color: var(--accent-2); border-color: rgba(0, 174, 255, .55); background: color-mix(in srgb, #00aeff 22%, var(--panel)); }
.rc.c-warn { color: #ffd882; border-color: rgba(254, 202, 87, .55); background: color-mix(in srgb, #feca57 18%, var(--panel)); }
.rc.c-red { color: #ff8f8f; border-color: rgba(255, 107, 107, .6); background: color-mix(in srgb, #ff6b6b 20%, var(--panel)); }
.rc.c-id { color: #b7d3e6; border-color: rgba(142, 181, 201, .5); background: color-mix(in srgb, #8eb5c9 16%, var(--panel)); }

.board-body { flex: 1; min-height: 0; display: flex; flex-direction: column; margin-top: 6px; }

/* 下钻单元的两张桌子：与实物一致上下排布，各占半屏 */
.pair-tables { flex: 1; min-height: 0; display: grid; grid-template-rows: repeat(2, minmax(0, 1fr)); gap: 12px; }

/* ---------- 桌子卡片 ---------- */
.d-table {
  --ts: var(--ok);
  min-width: 0; min-height: 0;
  display: flex; flex-direction: column; gap: 7px;
  padding: 8px 9px 9px;
  border: 1px solid var(--line); border-radius: 8px;
  /* 桌子卡片：纯色实底，不要渐变 */
  background: var(--card);
  transition: border-color .18s, box-shadow .18s;
}
.d-table.st-normal { --ts: var(--ok); border-color: rgb(29, 209, 161); }
.d-table.st-warning { --ts: var(--warn); border-color: rgba(254, 202, 87, .5); }
.d-table.st-error { --ts: var(--err); border-color: var(--err); }
.d-table.st-idle { --ts: var(--idle); border-style: dashed; }

.dt-head { flex: 0 0 22px; display: flex; align-items: center; gap: 9px; min-width: 0; }
.dt-code { font-family: var(--font-num); font-size: 14px; letter-spacing: .5px; color: #ffffff; white-space: nowrap; }
.dt-meta { font-size: 11.5px; color: var(--txt-dim); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dt-flag { margin-left: auto; font-size: 11.5px; color: var(--ts); white-space: nowrap; }

/* 与总览一一对应：一张桌一行，列数 = 该桌工位数（普通桌 6 列 → 上下两桌即 2 行 × 6 列） */
.dt-body { flex: 1; min-height: 0; display: grid; grid-template-columns: repeat(var(--cols, 6), minmax(0, 1fr)); grid-auto-rows: minmax(0, 1fr); gap: 8px; }

/* ---------- 工位卡片 ---------- */
.d-ws {
  --sc: var(--ok);
  --sc-soft: rgba(29, 209, 161, .3);
  position: relative; overflow: hidden;
  min-width: 0;
  /* 顶对齐：有提示 / 没提示都不影响标题、型号、信息行的起始位置 */
  display: flex; flex-direction: column; justify-content: flex-start; gap: 10px;
  padding: 14px 14px 16px;
  border: 1px solid var(--line); border-radius: 6px;
  /* 卡片自带状态色底：状态色直接混进深蓝卡片底，纯色、不透明
     正常绿 / 预警黄 / 异常红 / 空闲灰蓝 —— 底色由 --sc 派生，状态类只管换 --sc */
  background: color-mix(in srgb, var(--sc) 60%, var(--card));
  font-family: inherit; text-align: left; cursor: pointer;
  transition: border-color .15s, background .15s, box-shadow .15s, transform .15s;
}
/* 顶部状态色条（与总览工位小图标同一语言） */
.d-ws::before {
  content: ""; position: absolute; left: 0; right: 0; top: 0; height: 2px;
  background: var(--sc);
}
.d-ws:hover {
  z-index: 3;
  transform: translateY(-1px);
  border-color: var(--sc);
  /* 悬停：状态色比例提高，亮一档（仍是纯色） */
  background: color-mix(in srgb, var(--sc) 80%, var(--card));
}
.d-ws.on { border-color: var(--sc); box-shadow: 0 0 0 1px var(--sc); }

.d-ws.st-normal {
  --sc: var(--ok); --sc-soft: rgba(29, 209, 161, 0.595);
  border-color: rgb(29, 209, 161);
}
.d-ws.st-warning {
  --sc: var(--warn); --sc-soft: rgba(254, 201, 87, 0.742);
  border-color: rgb(254, 201, 87);
}
.d-ws.st-error {
  --sc: var(--err); --sc-soft: rgba(255, 107, 107, .42);
  border-color: var(--err);
  /* 异常卡红色占比最高：远看就是一块红（底仍是深蓝，不发黑） */
  background: color-mix(in srgb, var(--err) 48%, var(--card));
}
.d-ws.st-idle {
  --sc: var(--idle); --sc-soft: rgba(142, 181, 201, .26);
  border-style: dashed; border-color: rgba(142, 181, 201, .55);
  /* 空闲卡彩度最低：灰蓝实底，和作业中的彩色卡片一眼区分 */
  background: color-mix(in srgb, #8eb5c9 18%, var(--card));
}

/* min-height 固定标题行高：正常 / 预警 / 异常 / 空闲四类卡片的标题行等高 */
.dw-top { display: flex; align-items: center; gap: 8px; min-width: 0; min-height: 24px; }
/* 工位序号：跟随工位状态色的小徽标（04 / 05 …） */
.dw-no {
  flex: 0 0 auto;
  font-style: normal; font-family: var(--font-num);
  font-size: 12px; font-weight: 700; letter-spacing: .5px; line-height: 1.6;
  padding: 1px 6px; border-radius: 4px;
  color: var(--sc);
  border: 1px solid color-mix(in srgb, var(--sc) 60%, transparent);
  background: color-mix(in srgb, var(--sc) 20%, var(--card));
}
.dw-dot { width: 7px; height: 7px; border-radius: 50%; flex: 0 0 auto; background: var(--sc); }
.dw-stage { font-family: var(--font-num); font-size: 15px; font-weight: 700; letter-spacing: .5px; color: #ffffff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
/* 问题标记：产品有问题时在卡顶右侧显示一个感叹号（黄色醒目），没问题不渲染 */
.dw-state {
  margin-left: auto; flex: 0 0 auto;
  font-style: normal; font-size: 16px; font-weight: 800; line-height: 1;
  color: var(--err); white-space: nowrap;
  text-shadow: 0 0 10px color-mix(in srgb, var(--err) 55%, transparent);
}

.dw-model {
  /* 行高固定：空闲卡片字号略小，但行高一致，保证下方信息行的起始位置完全对齐 */
  line-height: 26px;
  font-family: var(--font-num); font-size: 21px; font-weight: 800; letter-spacing: .4px;
  color: #ffffff; text-shadow: 0 0 16px rgba(0, 174, 255, .45);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.d-ws.st-idle .dw-model { color: #dcecf8; font-weight: 600; font-size: 17px; text-shadow: none; }

/* 工件 / 人员 / 检验信息行：标签定宽、值左对齐，超长省略
   标签用 min-width:4em（标签都是四个汉字）而不是固定 px：
   固定 px 小于文字实际宽度时汉字会溢出压到值上，看起来像标题和内容重叠 */
.dw-kv { display: grid; gap: 5px; min-width: 0; font-size: 14.5px; color: #ffffff; }
.dw-kv > .kv-row {
  /* 标签 + 值 一行排：标签定宽、值占剩余空间（值区自己裁剪 / 滚动） */
  display: flex; align-items: center;
  min-width: 0; white-space: nowrap;
  line-height: 1.35;
}
.dw-kv em {
  flex: 0 0 auto;
  display: inline-block; min-width: 4em; margin-right: 8px;
  font-style: normal; color: #ffffffdb;;
}

/* ✅ 值区：超长时不做省略号截断，而是在行内缓缓滚动显示
   --kv-shift 由 v-marquee 指令量出来（超出宽度），只有真超宽才加 is-marquee */
.dw-kv .kv-val {
  flex: 1 1 auto; min-width: 0;
  display: block; overflow: hidden; white-space: nowrap;
  font-style: normal;   /* ⚠️ 值区原来是 <i> 标签，浏览器默认斜体；换成 span 后再兜一层 */
}
.dw-kv .kv-val > span { display: inline-block; white-space: nowrap; font-style: normal; }
/* ✅ 单向循环滚动：从头滚到尾，走完一轮立刻回到开头重新滚
   （去掉 alternate，alternate 是来回往返；这里要的是单向循环）
   ⚠️ 时长改由 var(--kv-dur) 决定：v-marquee 按「距离 ÷ 恒定速度」算好写进来，
      所以所有字段的滚动速度一致（字多的多滚一会儿，快慢看着一样）。
      想整体调快慢请改 JS 里的 MARQUEE_SPEED（每秒滚多少像素），不要再改这里的秒数。 */
.dw-kv .kv-val.is-marquee > span {
  animation: kvMarquee var(--kv-dur, 10s) linear infinite;
  will-change: transform;
}
@keyframes kvMarquee {
  0%, 12%    { transform: translateX(0); }                                  /* 先停一下，方便看清开头 */
  88%, 100%  { transform: translateX(calc(-1 * var(--kv-shift, 0px))); }    /* 滚到末尾停一下 → 下一轮从 0% 重新开始 */
}
/* 2 工位桌卡片很宽：信息行排成两列，避免横向空荡 */
.d-table.thin .dw-kv { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px 14px; }
/* 矮屏（笔记本 / 小屏）：卡片变矮时压缩行距字号，信息一行都不隐藏（避免"内容消失"的错觉） */
@media (max-height: 820px) {
  .dw-kv { gap: 4px; font-size: 11px; }
  .dw-kv em { min-width: 4em; margin-right: 6px; }
  /* 矮屏把放大后的状态 / 型号收一档，避免卡片内容被裁 */
  .dw-model { font-size: 18px; }
  .dw-state { font-size: 13px; }
}

/* 提示区：始终渲染、吸底、等高 —— 进度条 + 文字
   - margin-top:auto → 所有卡片的提示条都贴在底部同一位置
   - min-height 固定 → 各状态卡片的提示区高度完全一致
   - 进度条宽度 = 产品进程（正常 / 暂停 / 停工都按进程走），颜色跟工位状态色 */
.dw-note {
  margin-top: auto;
  box-sizing: border-box;
  display: flex; align-items: center; gap: 8px;
  min-height: 26px;
  padding: 5px 8px; border-radius: 4px;
  border-left: 2px solid var(--sc);
  /* 提示条底色加实且完全不透明：深蓝/彩色卡底上进度条和文字照样看得清 */
  background: color-mix(in srgb, var(--sc) 34%, var(--card));
}
.dw-bar {
  flex: 1; min-width: 0; height: 6px; border-radius: 3px;
  background: rgba(255, 255, 255, .18); overflow: hidden;
}
.dw-bar > i {
  display: block; height: 100%; border-radius: 3px;
  background: linear-gradient(90deg, color-mix(in srgb, var(--sc) 55%, #ffffff), var(--sc));
  transition: width .4s ease;
}
.dw-note-txt {
  flex: 0 0 auto; max-width: 62%;
  font-size: 11.5px; font-weight: 700; line-height: 1.4; color: #ffffff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
/* 空闲卡的占位提示：亮灰蓝，和作业中的彩色卡片区分开 */
.dw-note.is-free {
  border-left-color: rgba(142, 181, 201, .6);
  background: color-mix(in srgb, #8eb5c9 22%, var(--card));
}
.dw-note.is-free .dw-note-txt { color: #dcecf8; }

/* ---------- 工位详情弹层 ---------- */
.ws-mask {
  position: fixed; inset: 0; z-index: 80;
  display: grid; place-items: center;
  background: rgba(2, 14, 32, .72);
  backdrop-filter: blur(2px);
  animation: wmMaskIn .14s ease-out;
}
@keyframes wmMaskIn { from { opacity: 0; } to { opacity: 1; } }
.ws-modal {
  --sc: var(--ok);
  width: 760px; max-width: 94vw; max-height: 88vh; overflow: auto;
  padding: 0 16px 14px;
  border: 1px solid rgba(0, 174, 255, .5); border-radius: 12px;
  background: var(--panel);
  box-shadow: 0 24px 60px rgba(0, 0, 0, .55), 0 0 0 1px rgba(0, 174, 255, .2);
  animation: wmIn .16s ease-out;
}
@keyframes wmIn { from { opacity: 0; transform: translateY(8px) scale(.99); } to { opacity: 1; transform: none; } }
.ws-modal.st-normal { --sc: var(--ok); }
.ws-modal.st-warning { --sc: var(--warn); border-color: rgba(254, 202, 87, .6); }
.ws-modal.st-error { --sc: var(--err); border-color: rgba(255, 107, 107, .65); }
.ws-modal.st-idle { --sc: var(--idle); border-color: rgba(142, 181, 201, .5); }

.wm-head { display: flex; align-items: center; gap: 9px; padding: 13px 0 11px; border-bottom: 1px solid rgba(0, 174, 255, .25); }
.wm-dot { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto; background: var(--sc); }
.wm-id { font-family: var(--font-num); font-size: 15px; letter-spacing: .5px; color: #ffffff; }
.wm-sub { font-size: 11px; color: var(--txt-dim); white-space: nowrap; }
.wm-state {
  margin-left: auto; font-size: 11px; color: var(--sc);
  padding: 2px 10px; border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--sc) 60%, transparent);
  background: color-mix(in srgb, var(--sc) 20%, var(--panel));
  white-space: nowrap;
}
.wm-close {
  flex: 0 0 auto; width: 24px; height: 24px;
  display: grid; place-items: center;
  font-size: 11px; line-height: 1; font-family: inherit;
  color: var(--txt-dim); background: color-mix(in srgb, #00aeff 18%, var(--panel));
  border: 1px solid var(--line); border-radius: 50%; cursor: pointer; transition: .15s;
}
.wm-close:hover { color: #fff; border-color: rgba(255, 107, 107, .7); background: color-mix(in srgb, #ff6b6b 22%, var(--panel)); }

/* 弹层头部「产品详情」按钮：绑了产品就有，点开看部件 / 自检确认 / 自检记录 / 问题；
   产品有问题（isIssue）时按钮保持高亮并在前面带一个「!」 */
.wm-issue-btn {
  display: inline-flex; align-items: center; gap: 6px; flex: 0 0 auto;
  /* 不设 margin-left:auto：头部 .wm-state 已经是 auto，这里跟着它排在关闭按钮左边 */
  padding: 3px 9px; border-radius: 999px;
  font: inherit; font-size: 11.5px; font-weight: 700; letter-spacing: .3px; color: #ffe8b8;
  cursor: pointer; white-space: nowrap;
  border: 1px solid rgba(254, 202, 87, .55);
  background: color-mix(in srgb, var(--warn) 22%, var(--panel));
  transition: background .15s, border-color .15s, color .15s;
}
.wm-issue-btn em {
  display: grid; place-items: center;
  width: 14px; height: 14px; border-radius: 50%;
  font-style: normal; font-size: 10px; font-weight: 800; line-height: 1;
  color: #2a1c00; background: var(--warn);
}
.wm-issue-btn b { font-family: var(--font-num); font-weight: 700; }
.wm-issue-btn:hover { color: #fff; border-color: var(--warn); background: color-mix(in srgb, var(--warn) 34%, var(--panel)); }
.wm-issue-btn.on { color: #fff; border-color: var(--warn); background: color-mix(in srgb, var(--warn) 40%, var(--panel)); }
.wm-issue-btn:disabled { cursor: default; opacity: .6; }
.wm-issue-btn:disabled:hover { color: #ffe8b8; border-color: rgba(254, 202, 87, .55); background: color-mix(in srgb, var(--warn) 22%, var(--panel)); }

.wm-hero { display: grid; grid-template-columns: 1.4fr 1fr; gap: 10px; margin: 12px 0 10px; }
.wm-hero-main, .wm-hero-side {
  display: flex; flex-direction: column; gap: 6px;
  padding: 9px 11px; border-radius: 8px;
  border: 1px solid var(--line);
  background: color-mix(in srgb, #00aeff 14%, var(--card));
  cursor: help; /* 提示这一块可以点：点一下弹出完整信息（触摸屏同样有效） */
}
.wm-hero-label { font-size: 10px; letter-spacing: 1px; color: var(--txt-dim); }
.wm-hero-main b { font-family: var(--font-num); font-size: 19px; font-weight: 700; color: #ffffff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wm-hero-side b { font-family: var(--font-num); font-size: 14px; font-weight: 700; color: #ffffff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.wm-fields { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 9px 10px; }
.wm-field {
  display: flex; flex-direction: column; gap: 6px; min-width: 0;
  padding: 8px 10px; border-radius: 7px;
  border: 1px solid rgba(0, 174, 255, .22);
  background: color-mix(in srgb, #00aeff 12%, var(--panel));
  cursor: help; /* 值被省略号截断时，点一下这个框看完整信息 */
}
/* 按下给一点反馈，触摸屏上知道点中了（浮层随后弹出） */
.wm-field:active, .wm-hero-main:active, .wm-hero-side:active {
  border-color: var(--accent-2);
  background: color-mix(in srgb, #00aeff 26%, var(--panel));
}
/* 标题与值：显式行高，避免大字号值把上一行标题挤到重叠 */
.wm-field span { font-size: 10px; line-height: 1.4; letter-spacing: .5px; color: var(--txt-dim); white-space: nowrap; }
.wm-field b { font-family: var(--font-num); font-size: 12.5px; line-height: 1.35; font-weight: 600; color: #ffffff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* 底部 = 进度条 + 文字：与工位卡片的 dw-note 同款显示方式 */
.wm-note {
  margin-top: 12px; padding: 8px 11px; border-radius: 7px;
  display: flex; align-items: center; gap: 10px;
  background: color-mix(in srgb, var(--sc) 18%, var(--panel));
  border-left: 2px solid var(--sc);
  cursor: help; /* 点一下看完整状态文案 */
}
.wm-bar {
  flex: 1; min-width: 0; height: 8px; border-radius: 4px;
  background: rgba(255, 255, 255, .18); overflow: hidden;
}
.wm-bar > i {
  display: block; height: 100%; border-radius: 4px;
  background: linear-gradient(90deg, color-mix(in srgb, var(--sc) 55%, #ffffff), var(--sc));
  transition: width .4s ease;
}
.wm-note-txt {
  flex: 0 0 auto; max-width: 60%;
  font-size: 12px; font-weight: 700; line-height: 1.4; color: #ffffff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.wm-note.is-free .wm-note-txt { color: #dcecf8; }

/* ---------- 底部告警滚动条 ---------- */
.ticker { flex: 0 0 30px; display: flex; align-items: center; gap: 12px; padding: 0 14px; }
.ticker-label { display: flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700; color: #ffffff; white-space: nowrap; }
.ticker-label i { width: 6px; height: 6px; border-radius: 50%; background: var(--err); animation: tkBlink 1.2s infinite; }
@keyframes tkBlink { 50% { opacity: .3; } }
.ticker-viewport {
  flex: 1; overflow: hidden;
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent);
          mask-image: linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent);
}
.ticker-track { display: inline-flex; white-space: nowrap; will-change: transform; animation: gasTickerMove 40s linear infinite; }
@keyframes gasTickerMove { to { transform: translateX(-50%); } }
/* background 兜底：ProcessDesign.vue 的全局 .error 会命中本页 tk.error，需显式声明透明底 */
.tk { font-size: 11px; color: #cdddf7; margin-right: 52px; display: inline-flex; align-items: center; white-space: nowrap; background: transparent; }
.tk b { font-family: var(--font-num); font-weight: 400; color: var(--txt-dim); margin-right: 8px; }
.tk::before { content: "●"; font-size: 9px; margin-right: 7px; }
.tk.error { color: #ffb3b3; } .tk.error::before { color: var(--err); }
.tk.warning { color: #ffe08a; } .tk.warning::before { color: var(--warn); }
.tk.info { color: #8ee6ff; } .tk.info::before { color: var(--accent); }
.tk.done { color: #7bf0c0; } .tk.done::before { color: var(--ok); }

/* ---------- 产品详情弹窗（ProductPartInfoDialog：部件 / 自检确认 / 自检记录 / 问题）----------
   组件本体是 Element 默认白底 + 蓝主色，这里用 ::v-deep 只在本页覆盖成驾驶舱暗色；
   颜色全部取 .gas-detail 上的同一套变量，和页面里的工位详情弹层（.ws-modal）保持一套皮 */
.parts-dialog-dark { background: rgba(2, 14, 32, .72); backdrop-filter: blur(2px); }
.parts-dialog-dark ::v-deep .el-dialog {
  border: 1px solid rgba(0, 174, 255, .5); border-radius: 12px;
  background: var(--panel);
  box-shadow: 0 24px 60px rgba(0, 0, 0, .55), 0 0 0 1px rgba(0, 174, 255, .2);
  animation: wmIn .16s ease-out;
}
.parts-dialog-dark ::v-deep .el-dialog__header {
  padding: 13px 16px 11px;
  border-bottom: 1px solid rgba(0, 174, 255, .25);
}
.parts-dialog-dark ::v-deep .el-dialog__title,
.parts-dialog-dark ::v-deep .materialDetailTitle_title {
  color: #ffffff; font-size: 15px; font-weight: 600; letter-spacing: .5px;
}
.parts-dialog-dark ::v-deep .el-dialog__body { padding: 12px 16px 16px; color: var(--txt); }
/* 右上角关闭按钮：换成工位弹层那个圆形「✕」的样子 */
.parts-dialog-dark ::v-deep .el-dialog__headerbtn {
  top: 13px; right: 13px; width: 24px; height: 24px;
  display: grid; place-items: center; border-radius: 50%;
  background: color-mix(in srgb, #00aeff 18%, var(--panel));
  transition: .15s;
}
.parts-dialog-dark ::v-deep .el-dialog__headerbtn .el-dialog__close { font-size: 13px; color: var(--txt-dim); }
.parts-dialog-dark ::v-deep .el-dialog__headerbtn:hover { background: color-mix(in srgb, #ff6b6b 22%, var(--panel)); }
.parts-dialog-dark ::v-deep .el-dialog__headerbtn:hover .el-dialog__close { color: #ffffff; }

/* 导出按钮：去掉 Element 蓝，换成页面同款青色描边 */
.parts-dialog-dark ::v-deep .export-button.el-button--primary {
  padding: 4px 10px; border-radius: 999px; font-size: 11.5px; color: #eaf6ff;
  border: 1px solid var(--line);
  background: color-mix(in srgb, var(--accent-2) 22%, var(--panel));
}
.parts-dialog-dark ::v-deep .export-button.el-button--primary:hover {
  color: #ffffff; border-color: var(--accent-2);
  background: color-mix(in srgb, var(--accent-2) 34%, var(--panel));
}

/* 四个列表的 Tab：次要文字 + 亮青选中 + 青色下划线 */
.parts-dialog-dark ::v-deep .el-tabs__item { color: var(--txt-dim); font-size: 12.5px; }
.parts-dialog-dark ::v-deep .el-tabs__item:hover,
.parts-dialog-dark ::v-deep .el-tabs__item.is-active { color: var(--accent-2); }
.parts-dialog-dark ::v-deep .el-tabs__active-bar { background: var(--accent-2); }
.parts-dialog-dark ::v-deep .el-tabs__nav-wrap::after { background: var(--line-soft); }

/* 表格：透明底 + 白字 + 青色表头
   card-table 内部用 !important 写了表头底色，这里同样带 !important，并多套一层 .el-table 提高优先级 */
.parts-dialog-dark ::v-deep .el-table,
.parts-dialog-dark ::v-deep .el-table tr,
.parts-dialog-dark ::v-deep .el-table__expanded-cell { background-color: transparent; color: #ffffff; }
.parts-dialog-dark ::v-deep .el-table .el-table__header thead th {
  background-color: color-mix(in srgb, #00aeff 16%, var(--panel)) !important;
  color: #ffffff; font-size: 11.5px; font-weight: 600;
  border-bottom: 1px solid var(--line-soft);
}
/* 表头最右侧的滚动条占位列（gutter）默认也是白底，一并压掉 */
.parts-dialog-dark ::v-deep .el-table .el-table__header th.gutter {
  background-color: color-mix(in srgb, #00aeff 16%, var(--panel)) !important;
}
/* 表头列之间的分隔竖线：card-table 里给的是 #dcdfe6（亮灰白），在暗底上太扎眼，
   这里只在本页弹窗换成灰蓝（--txt-dim 同色系降透明），和表头底色揉在一起 */
.parts-dialog-dark ::v-deep .el-table__header-wrapper th {
  border-right: 1px solid rgba(142, 181, 201, .3);
}
.parts-dialog-dark ::v-deep .el-table td.el-table__cell {
  background-color: transparent; color: #ffffff; font-size: 11.5px;
  border-bottom: 1px solid var(--line-soft);
}
.parts-dialog-dark ::v-deep .el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell,
.parts-dialog-dark ::v-deep .el-table__body tr.hover-row > td.el-table__cell {
  background-color: color-mix(in srgb, #00aeff 12%, var(--panel));
}
/* 表格底部那条线（Element 用它画边框），换成驾驶舱的淡青 */
.parts-dialog-dark ::v-deep .el-table::before { height: 1px; background-color: var(--line-soft); }
.parts-dialog-dark ::v-deep .el-table .el-table__empty-text { color: var(--txt-dim); }
/* 滚动条跟页面其它滚动区同款 */
.parts-dialog-dark ::v-deep .el-table__body-wrapper::-webkit-scrollbar { width: 8px; height: 8px; }
.parts-dialog-dark ::v-deep .el-table__body-wrapper::-webkit-scrollbar-track { background: rgba(0, 174, 255, .08); }
.parts-dialog-dark ::v-deep .el-table__body-wrapper::-webkit-scrollbar-thumb {
  background: rgba(0, 174, 255, .45); border-radius: 4px;
  border: 2px solid transparent; background-clip: padding-box;
}
.parts-dialog-dark ::v-deep .el-table__body-wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 174, 255, .75); background-clip: padding-box;
}
</style>

<!-- 「完整信息」浮层：节点挂在 body 上（不在组件模板里），所以样式不能写在 scoped 里，
     用独立的非 scoped 样式块，类名 .gas-tip 唯一，不会影响其它页面 -->
<style>
.gas-tip {
  position: fixed;
  z-index: 3000; /* 要盖住工位详情弹层（.ws-mask） */
  max-width: 320px;
  padding: 7px 10px;
  border: 1px solid rgba(0, 174, 255, .55);
  border-radius: 8px;
  background: rgba(6, 22, 40, .96);
  box-shadow: 0 8px 22px rgba(0, 0, 0, .45);
  color: #eaf6ff;
  font-size: 12px;
  line-height: 1.5;
  letter-spacing: .3px;
  word-break: break-all;
  pointer-events: none; /* 不挡点击：触屏点别处仍能立刻关掉，也不会挡住下面的按钮 */
  opacity: 0;
  visibility: hidden;
  transition: opacity .15s ease;
}
.gas-tip.on { opacity: 1; visibility: visible; }
</style>
